import { apiService } from './api';
import { Trip, CreateTripData, Payment, CreatePaymentData } from '../types';
import { isMockMode, mockDelay, mockLog } from '../utils/mockHelper';
import { getMockTrips, getMockTripById, addMockTrip } from '../data/mockData';

export class TripService {
  async getTrips(): Promise<Trip[]> {
    if (isMockMode()) {
      return this.mockGetTrips();
    }
    
    try {
      console.log('🔄 TripService: Fetching trips...');
      const response = await apiService.get<any>('/trips');
      console.log('✅ TripService: Trips response:', response.data);
      
      // Handle paginated response
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log('✅ TripService: Trips fetched successfully (paginated):', response.data.data);
        return response.data.data;
      }
      
      // Handle direct array response
      if (Array.isArray(response.data)) {
        console.log('✅ TripService: Trips fetched successfully (array):', response.data);
        return response.data;
      }
      
      console.log('⚠️ TripService: Unexpected response format, returning empty array');
      return [];
    } catch (error: any) {
      console.error('❌ TripService: Error fetching trips:', error);
      
      // If the API endpoint doesn't exist (404), return empty array instead of throwing
      if (error.response?.status === 404) {
        console.log('⚠️ TripService: Trips endpoint not found, returning empty array');
        return [];
      }
      
      throw new Error(error.response?.data?.message || 'Failed to fetch trips');
    }
  }

  private async mockGetTrips(): Promise<Trip[]> {
    mockLog('get trips');
    await mockDelay();
    return getMockTrips();
  }

  async getTrip(id: number): Promise<Trip> {
    if (isMockMode()) {
      return this.mockGetTrip(id);
    }
    
    try {
      const response = await apiService.get<Trip>(`/trips/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trip');
    }
  }

  private async mockGetTrip(id: number): Promise<Trip> {
    mockLog('get trip', id);
    await mockDelay(200, 400);
    
    const trip = getMockTripById(id);
    if (!trip) {
      throw new Error('Viagem não encontrada');
    }
    
    return trip;
  }

  async createTrip(tripData: CreateTripData): Promise<Trip> {
    if (isMockMode()) {
      return this.mockCreateTrip(tripData);
    }
    
    try {
      const response = await apiService.post<any>('/trips', tripData);
      // The API returns { message: '...', trip: {...} }
      if (response.data && response.data.trip) {
        return response.data.trip;
      }
      // If the API returns the trip directly
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create trip';
      const errors = error.response?.data?.errors;
      
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(', ');
        throw new Error(`${message}: ${errorMessages}`);
      }
      
      throw new Error(message);
    }
  }

  private async mockCreateTrip(tripData: CreateTripData): Promise<Trip> {
    mockLog('create trip', tripData);
    await mockDelay(800, 1200);
    
    // Calculate total amount based on participants
    const totalAmount = tripData.participants.reduce((sum, participant) => {
      if (participant.exemption_reason_id) return sum;
      // Mock: adults pay 30, children under 12 pay 15
      return sum + 30; // Simplified for demo
    }, 0);
    
    const newTrip = addMockTrip({
      hotel_name: tripData.hotel_name,
      start_date: tripData.start_date,
      end_date: tripData.end_date,
      total_days: this.calculateDays(tripData.start_date, tripData.end_date),
      total_amount: totalAmount,
      participants: tripData.participants.map(p => ({
        id: Math.floor(Math.random() * 1000000),
        trip_id: 0, // Will be set by addMockTrip
        name: p.name,
        email: p.email || null,
        document_number: p.document_number,
        date_of_birth: p.date_of_birth,
        phone: p.phone || null,
        country_code: p.country_code || '+55',
        individual_amount: p.exemption_reason_id ? 0 : 30,
        is_exempt: !!p.exemption_reason_id,
        exemption_reason_id: p.exemption_reason_id,
        voucher_code: `JER${new Date().getFullYear()}${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }))
    });
    
    return newTrip;
  }

  private calculateDays(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  async updateTrip(id: number, tripData: Partial<CreateTripData>): Promise<Trip> {
    if (isMockMode()) {
      return this.mockUpdateTrip(id, tripData);
    }
    
    try {
      const response = await apiService.put<Trip>(`/trips/${id}`, tripData);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update trip';
      const errors = error.response?.data?.errors;
      
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(', ');
        throw new Error(`${message}: ${errorMessages}`);
      }
      
      throw new Error(message);
    }
  }

  private async mockUpdateTrip(id: number, tripData: Partial<CreateTripData>): Promise<Trip> {
    mockLog('update trip', { id, tripData });
    await mockDelay(600, 1000);
    
    const trip = getMockTripById(id);
    if (!trip) {
      throw new Error('Viagem não encontrada');
    }
    
    // In a real mock implementation, we would update the trip in our mock data
    // For now, just return the existing trip
    return trip;
  }

  async deleteTrip(id: number): Promise<void> {
    if (isMockMode()) {
      return this.mockDeleteTrip(id);
    }
    
    try {
      await apiService.delete(`/trips/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete trip');
    }
  }

  private async mockDeleteTrip(id: number): Promise<void> {
    mockLog('delete trip', id);
    await mockDelay(400, 600);
    
    // In a real mock implementation, we would remove the trip from our mock data
    // For now, just simulate success
  }

  async createPayment(paymentData: CreatePaymentData): Promise<Payment> {
    if (isMockMode()) {
      return this.mockCreatePayment(paymentData);
    }
    
    try {
      const response = await apiService.post<Payment>('/payments', paymentData);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create payment';
      const errors = error.response?.data?.errors;
      
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(', ');
        throw new Error(`${message}: ${errorMessages}`);
      }
      
      throw new Error(message);
    }
  }

  private async mockCreatePayment(paymentData: CreatePaymentData): Promise<Payment> {
    mockLog('create payment', paymentData);
    await mockDelay(1000, 1500);
    
    const mockPayment: Payment = {
      id: Math.floor(Math.random() * 1000000),
      trip_id: paymentData.trip_id,
      amount: paymentData.amount,
      payment_method: paymentData.payment_method,
      gateway_transaction_id: `mock_txn_${Date.now()}`,
      status: 'completed',
      processed_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    return mockPayment;
  }

  async confirmPayment(tripId: number, paymentData: { gateway_transaction_id: string; payment_method: string }): Promise<Trip> {
    if (isMockMode()) {
      return this.mockConfirmPayment(tripId, paymentData);
    }
    
    try {
      const response = await apiService.post<any>(`/trips/${tripId}/pay`, paymentData);
      // The API returns { message: '...', trip: {...}, payment: {...} }
      return response.data.trip || response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to confirm payment');
    }
  }

  private async mockConfirmPayment(tripId: number, paymentData: any): Promise<Trip> {
    mockLog('confirm payment', { tripId, paymentData });
    await mockDelay(800, 1200);
    
    const trip = getMockTripById(tripId);
    if (!trip) {
      throw new Error('Viagem não encontrada');
    }
    
    // Update payment status to paid
    trip.payment_status_id = 2; // PAID
    return trip;
  }

  async getTripQRCode(tripId: number): Promise<string> {
    if (isMockMode()) {
      return this.mockGetTripQRCode(tripId);
    }
    
    try {
      const response = await apiService.get<{ qr_code: string }>(`/trips/${tripId}/qr-code`);
      return response.data.qr_code;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get QR code');
    }
  }

  private async mockGetTripQRCode(tripId: number): Promise<string> {
    mockLog('get trip qr code', tripId);
    await mockDelay(300, 500);
    
    const trip = getMockTripById(tripId);
    if (!trip) {
      throw new Error('Viagem não encontrada');
    }
    
    // Return voucher code as QR data
    return trip.voucher_code || `JER${tripId}MOCK`;
  }
}

export const tripService = new TripService();
